import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bookmark, Network, Trash2, Layers } from "lucide-react";
import { fetchConnection, api } from "../lib/api";
import { earnCoins } from "../lib/coins";
import { isConnectionSaved, toggleConnectionSaved } from "../lib/bookmarks";
import OpenInTiesButton from "../components/OpenInTiesButton";
import StrongestLinkShareButton from "../components/StrongestLinkShareButton";
import CiteInThreadButton from "../components/CiteInThreadButton";
import ArtifactExtras from "../components/ArtifactExtras";
import { isAdmin, adminHeader } from "../lib/admin";
import { renderMarkdown } from "../lib/markdown";
import Comments from "../components/Comments";
import WitnessStatements from "../components/WitnessStatements";
import PredictionPanel from "../components/PredictionPanel";
import CommunityFacts from "../components/CommunityFacts";
import BeliefSlider from "../components/BeliefSlider";
import CrossReferences from "../components/CrossReferences";
import ConnectionChat from "../components/ConnectionChat";
import { getConnectMode } from "../lib/connectionMode";
import ConnectionGraph from "../components/ConnectionGraph";
import PrintButton from "../components/PrintButton";
import ReactionButtons from "../components/ReactionButtons";
import ShareButton from "../components/ShareButton";
import CosignButton from "../components/CosignButton";
import PrevNextNav from "../components/PrevNextNav";
import { useTrackPageView } from "../hooks/useAnalytics";
import { toast } from "sonner";

export default function ConnectionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState(false);
  const [admin, setAdmin] = useState(isAdmin());

  useTrackPageView(`/connection/${id}`, { target_type: "connection", target_id: id });

  useEffect(() => {
    const h = () => setAdmin(isAdmin());
    window.addEventListener("admin-changed", h);
    return () => window.removeEventListener("admin-changed", h);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchConnection(id)
      .then((d) => {
        setConn(d);
        setSaved(isConnectionSaved(id));
        // Coin award — rate-limited server-side; failures silent.
        earnCoins("browse_dossier", `conn:${id}`).then((r) => {
          if (r?.awarded > 0) window.dispatchEvent(new Event("coins-changed"));
        });
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const onSave = () => {
    const next = toggleConnectionSaved(id);
    setSaved(next);
    toast.success(next ? "Saved to your library" : "Removed from your library");
  };

  const onAdminDelete = async () => {
    if (!window.confirm("Permanently DELETE this connection? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/connect-dots/${id}`, { headers: adminHeader() });
      toast.success("Connection deleted");
      navigate("/archive");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-24">
        <div className="font-mono text-xs tracking-wider text-zinc-500">LOADING CONNECTION...</div>
      </div>
    );
  }
  if (err || !conn) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-24">
        <div className="font-mono text-xs tracking-wider text-[#ff3b30] mb-4">CONNECTION NOT FOUND</div>
        <Link to="/archive" className="font-mono text-sm text-white underline">← Return to Archive</Link>
      </div>
    );
  }

  return (
    <article data-testid="connection-page" className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <Link to="/archive" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-zinc-500 hover:text-white mb-10">
        <ArrowLeft size={12} /> ARCHIVE INDEX
      </Link>

      <div className="bg-[#0F0F0F] border-t-4 border-t-pink-400 border-x border-b border-white/10 p-6 md:p-10">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10 flex-wrap gap-3">
          <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] text-zinc-500 flex-wrap">
            <Network size={12} />
            CONNECTION ANALYSIS // ID {conn.id.slice(0, 8).toUpperCase()}
            {conn.mode && conn.mode !== "default" && (() => {
              const m = getConnectMode(conn.mode);
              return (
                <span
                  data-testid="connection-mode-badge"
                  className="ml-1 inline-flex items-center border px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase"
                  style={{ borderColor: `${m.color}99`, color: m.color, background: `${m.color}1a` }}
                >
                  {m.short} MODE
                </span>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onSave}
              data-testid="connection-save"
              className={`inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                saved
                  ? "border-[#ffd60a]/50 text-[#ffd60a] bg-[#ffd60a]/5"
                  : "border-white/15 text-zinc-400 hover:text-[#ffd60a] hover:border-[#ffd60a]/50"
              }`}
            >
              <Bookmark size={12} fill={saved ? "#ffd60a" : "none"} />
              {saved ? "Saved" : "Save"}
            </button>
            <OpenInTiesButton kind="connection" ref={conn.id} sourceMode={conn?.mode || getConnectMode()} variant="ghost" size="sm" testid="connection-open-ties" />
            <StrongestLinkShareButton kind="connection" ref={conn.id} testid="connection-share-card" />
            <CiteInThreadButton kind="connection" ref={conn.id} title={conn.title} url={`/connection/${conn.id}`} testid="connection-cite-thread" />
            <PrintButton />
            {admin && (
              <button
                onClick={onAdminDelete}
                data-testid="admin-delete-connection"
                className="inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 border-white/15 hover:text-[#ff3b30] hover:border-[#ff3b30]/50 transition-colors"
              >
                <Trash2 size={11} /> Delete
              </button>
            )}
          </div>
        </div>

        <h1 className="font-heading font-black uppercase tracking-tighter text-3xl sm:text-4xl text-white leading-[0.95] mb-4">
          {conn.title}
        </h1>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="font-mono text-[10px] tracking-[0.18em] text-pink-400 border border-pink-400/40 bg-pink-400/5 px-2 py-1">
            CONNECTED — {conn.items.length} SUBJECTS
          </span>
          <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
            COMPILED — {new Date(conn.created_at).toISOString().slice(0, 10)}
          </span>
        </div>

        <div className="flex flex-wrap gap-1 mb-8 pb-6 border-b border-white/10">
          {conn.items.map((it, i) => (
            <span key={i} className="font-mono text-[11px] tracking-wider uppercase text-zinc-300 border border-white/15 bg-white/5 px-2 py-1">
              {it}
            </span>
          ))}
        </div>

        <ConnectionGraph items={conn.items} title={conn.title} />

        <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <ReactionButtons targetType="connection" targetId={conn.id} />
            {/* Co-signs surface on every connection page; the button itself
                handles the disabled state when the author opted out. */}
            <CosignButton targetType="connection" targetId={conn.id} authorId={conn.user_id} />
          </div>
          <ShareButton title={conn.title} testId="connection-share" />
        </div>

        <div className="border border-[#ff3b30]/30 bg-[#ff3b30]/5 px-4 py-3 mb-8 font-mono text-[10px] tracking-wider text-[#ff3b30] leading-relaxed">
          NOTICE — AI-compiled analysis. Connections may be speculative. Verify all claims independently.
        </div>

        <div className="markdown text-zinc-300" dangerouslySetInnerHTML={{ __html: renderMarkdown(conn.analysis) }} />

        {/* Continuous chat — only shown when arriving via the library (?chat=1).
            Hidden from public/archive visits so the published view stays clean. */}
        {(() => {
          try {
            const qs = new URLSearchParams(window.location.search);
            if (qs.get("chat") === "1") {
              return <ConnectionChat connection={conn} onUpdate={(updated) => setConn(updated)} />;
            }
          } catch { /* ignore */ }
          return null;
        })()}

        {conn.tavily_sources && conn.tavily_sources.length > 0 && (
          <details
            data-testid="tavily-sources-panel"
            className="mt-8 border border-[#60a5fa]/30 bg-[#0a0a0a] p-4"
          >
            <summary className="cursor-pointer font-mono text-[10px] tracking-[0.25em] text-[#60a5fa] flex items-center gap-2 hover:text-white transition-colors">
              ▸ LIVE WEB SOURCES — {conn.tavily_sources.length} · TAVILY · PULLED AT ANALYSIS TIME
            </summary>
            <ul className="mt-3 space-y-3">
              {conn.tavily_sources.map((s, i) => (
                <li key={`${s.url}-${i}`} className="border-l-2 border-[#60a5fa]/40 pl-3">
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                     className="block font-mono text-[11px] tracking-wide text-[#60a5fa] hover:underline break-all">
                    {s.title || s.url}
                  </a>
                  <div className="flex items-center gap-2 mt-0.5 font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase">
                    {s.source && <span>{s.source}</span>}
                    {s.published_date && <span>· {s.published_date}</span>}
                  </div>
                  {s.content && (
                    <p className="mt-1 text-[12px] text-zinc-400 leading-relaxed line-clamp-4">
                      {s.content.length > 280 ? s.content.slice(0, 280).trimEnd() + "…" : s.content}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}

        {/* EXPANSIONS — appended depth passes. Skip placeholders. */}
        {conn.expansions && conn.expansions.filter((e) => e.status !== "generating" && e.status !== "error" && (e.report || "").length > 0).length > 0 && (
          <div data-testid="conn-expansions-panel" className="mt-10 space-y-8">
            {conn.expansions
              .filter((e) => e.status !== "generating" && e.status !== "error" && (e.report || "").length > 0)
              .map((exp, idx) => (
              <div
                key={idx}
                data-testid={`conn-expansion-${idx + 1}`}
                className="border-l-2 border-[#a78bfa] pl-5"
              >
                <div className="font-mono text-[10px] tracking-[0.25em] mb-3 flex items-center gap-2 text-[#a78bfa]">
                  <Layers size={11} /> EXPANSION PASS {exp.level || idx + 1} —{" "}
                  {exp.generated_at ? new Date(exp.generated_at).toISOString().slice(0, 10) : ""}
                </div>
                <div
                  className="markdown text-zinc-300"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(exp.report || "") }}
                />
              </div>
            ))}
          </div>
        )}

        {/* No expand button on public pages — bulk expansion is admin-only via the dashboard. */}
      </div>

      <ArtifactExtras kind="connection" ref={conn.id} subjects={conn.items} />

      <BeliefSlider targetType="connection" targetId={conn.id} />
      <CommunityFacts targetType="connection" targetId={conn.id} />
      <CrossReferences targetType="connection" targetId={conn.id} />
      <PrevNextNav
        type="connection"
        currentId={conn.id}
        urlBuilder={(id) => `/connection/${id}`}
        accent="#ec4899"
        testIdPrefix="connection-nav"
      />
      <Comments targetType="connection" targetId={conn.id} />
      <WitnessStatements targetType="connection" targetId={conn.id} />
      <PredictionPanel targetType="connection" targetId={conn.id} />
    </article>
  );
}
